import { ChangeDetectorRef, Component, ElementRef, Injector, Input, Optional, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { FormBuilder, FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatColumnDef, MatTable } from '@angular/material/table';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Columns, FormStorage, PageSpecification, toEndOfDayDate, toPageSpecification, toStartOfDayDate, UserStorage } from '@scisys-uim/uim-common';
import { Asset, Firearm, FIREARM_POOL_TYPE, FirearmLifecycleStatus } from 'assets-model';
import { AssetService, FirearmService, RefCodeStorage } from 'assets-service';
import { ASSET_SEARCH_FILTERS_DEBOUNCE_TIME, ASSET_SEARCH_RESULT_DEFAULT_DISABLED_COLUMNS, ASSET_SEARCH_RESULT_DISPLAYED_COLUMNS, AssetSearchFiltersComponent, assetSearchResultDisplayedColumnsExcluding } from '../../asset/search/asset-search.component';
import { FirearmSearchComponentBase } from './firearm-search.component.base';
import { debounceTime } from 'rxjs/operators';
import { MatSelect } from '@angular/material/select';


@Component({
  selector: 'app-firearm-search-result-columns',
  templateUrl: './firearm-search-result-columns.component.html',
  styleUrls: ['./firearm-search.component.scss']
})
export class FirearmSearchResultColumnsComponent {
  @ViewChildren(MatColumnDef) columnDefs: QueryList<MatColumnDef>;

  constructor(@Optional() public table: MatTable<any>, private cdRef: ChangeDetectorRef) { }

  @Input()
  columns: Columns;

  @Input()
  cellClassFn = (element: Firearm, fieldName: any) => { };

  @Input()
  assetGetter: (record: any) => Asset;

  @Input()
  firearmGetter: (record: any) => Firearm;

  ngOnInit() {
    if (!this.assetGetter) {
      this.assetGetter = (record: any) => record?.asset;
    }

    if (!this.firearmGetter) {
      this.firearmGetter = (record: any) => record;
    }

    if (this.table) {
      this.cdRef.detectChanges();
      this.columnDefs.forEach(columnDef => {
        this.table.addColumnDef(columnDef);
      });
    }
  }


  cellClass(element: Firearm, fieldName: string) {
    return this.cellClassFn(element, fieldName);
  }
}

export const FIREARM_SEARCH_BASIC_COLUMNS = ['firearmType', 'calibre', 'personalFirearm',]
export const FIREARM_SEARCH_FORMULA_COLUMNS = ['poolFirearm', 'currentlyAssigned', 'currentlyAssignable', 'suspended', 'lifecycleState']

export const FIREARM_SEARCH_RESULT_DEFAULT_DISPLAYED_COLUMNS = [...FIREARM_SEARCH_BASIC_COLUMNS, ...FIREARM_SEARCH_FORMULA_COLUMNS];

@Component({
  selector: 'app-firearm-search',
  templateUrl: './firearm-search.component.html',
  styleUrls: ['./firearm-search.component.scss']
})
export class FirearmSearchComponent extends FirearmSearchComponentBase {

  displayedColumns: string[] = ['assetId', 'businessUnit.buId', 'costCentre.ccId', 'currentAssignmentFirearmAssignmentType', 'currentAssignmentEmail', ...FIREARM_SEARCH_RESULT_DEFAULT_DISPLAYED_COLUMNS, ...assetSearchResultDisplayedColumnsExcluding(['businessUnit.buId', 'costCentre.ccId', 'assetId', 'assetType.assetTypeId', 'status']), 'actions'];
  disabledColumns: string[] = [...ASSET_SEARCH_RESULT_DEFAULT_DISABLED_COLUMNS, 'lifecycleState', 'currentlyAssigned', 'currentlyAssignable', 'suspended']

  lifecycleStatusesFilter: FirearmLifecycleStatus[] = ['IN_SERVICE', 'SUSPENDED', 'PENDING'];
  @ViewChild('lifecycleStatusesFilterInput') lifecycleStatusesFilterInput: MatSelect;

  disposalDateGreaterThanOrEqualToFilter: Date;
  @ViewChild('disposalDateGreaterThanOrEqualToFilterInput') disposalDateGreaterThanOrEqualToFilterInput: ElementRef;

  disposalDateLessThanOrEqualToFilter: Date;
  @ViewChild('disposalDateLessThanOrEqualToFilterInput') disposalDateLessThanOrEqualToFilterInput: ElementRef;

  constructor(protected route: ActivatedRoute,
    protected router: Router,
    protected userStorage: UserStorage,
    protected dialog: MatDialog,
    protected firearmService: FirearmService,
    protected assetService: AssetService,
    protected refCodeStorage: RefCodeStorage,
    protected fb: FormBuilder,
    protected formStorage: FormStorage,
    protected injector: Injector) {
    super(route, router, userStorage, dialog, firearmService, assetService, refCodeStorage, fb, formStorage, injector);
    this.searchForm.addControl('lifecycleStatuses', new FormControl(this.lifecycleStatusesFilter));
    this.searchForm.addControl('disposalDateGreaterThanOrEqualTo', new FormControl(null));
    this.searchForm.addControl('disposalDateLessThanOrEqualTo', new FormControl(null));
  }

  @ViewChild(AssetSearchFiltersComponent) filtersComponent: AssetSearchFiltersComponent;

  @Input()
  assetGetter: (record: any) => Asset;

  ngOnInit() {
    super.ngOnInit();
    if (!this.assetGetter) {
      this.assetGetter = (record: any) => record?.asset;
    }

  }

  protected onInitAsyncComplete() {
    super.onInitAsyncComplete();
    this.calibreLoadSelectionList();
    this.firearmTypeLoadSelectionList();
    this.lifecycleStateLoadSelectionList();
    this.currentAssignmentFirearmAssignmentTypeLoadSelectionList();

    if (!this.restoreFormFromParams(this.searchForm)) {
      this.resetSearchForm();
      this.restoreFormChanges();
    }
  }

  ngAfterViewInit(): void {
    super.ngAfterViewInit();
    this.addSearchOnChangedSelectEventListener(this.lifecycleStatusesFilterInput);
    this.addSearchOnChangedInputEventListener(this.disposalDateGreaterThanOrEqualToFilterInput);
    this.addSearchOnChangedInputEventListener(this.disposalDateLessThanOrEqualToFilterInput);
    this.filtersComponent?.serviceArgumentsChange.pipe(
      debounceTime(ASSET_SEARCH_FILTERS_DEBOUNCE_TIME)
    ).subscribe(args => {
      this.maybeDoAutoSearch();
    });
  }

  resetSearchForm() {
    this.filtersComponent?.resetSearchForm();
  }

  clearFilters() {
    super.clearFilters();
    this.filtersComponent?.clearFilters();
  }

  clearFilterVariables() {
    super.clearFilterVariables();
    this.disposalDateGreaterThanOrEqualToFilter = null;
    this.disposalDateLessThanOrEqualToFilter = null;
  }

  createServiceArguments() {
    const assetFilterArgs: any = this.filtersComponent?.createServiceArguments() || <any>{};
    const serviceArgs: any = super.createServiceArguments();
    serviceArgs.assetFilter = assetFilterArgs;
    serviceArgs.lifecycleStatuses = [...this.lifecycleStatusesFilter];
    serviceArgs.disposalDateGreaterThanOrEqualTo = toStartOfDayDate(this.disposalDateGreaterThanOrEqualToFilter);
    serviceArgs.disposalDateLessThanOrEqualTo = toEndOfDayDate(this.disposalDateLessThanOrEqualToFilter);
    return serviceArgs;
  }

  lifecycleStatusesChanging(value: FirearmLifecycleStatus[], unsetDependents: boolean = true) { }

  lifecycleStatusesChanged(value: FirearmLifecycleStatus[], unsetDependents: boolean = true) {
    /*
    if (unsetDependents) {
      this.depdendentField.setValue(null);
    }
    dependentFieldLoadSelectionList();
    */
  }

  disposalDateGreaterThanOrEqualToChanging(value: Date, unsetDependents: boolean = true) { }

  disposalDateGreaterThanOrEqualToChanged(value: Date, unsetDependents: boolean = true) { }

  disposalDateLessThanOrEqualToChanging(value: Date, unsetDependents: boolean = true) { }

  disposalDateLessThanOrEqualToChanged(value: Date, unsetDependents: boolean = true) { }

  isAssetColumn(column: string) {
    return ASSET_SEARCH_RESULT_DISPLAYED_COLUMNS.includes(column);
  }

  pageSpecification() {
    var pageSpec: PageSpecification = super.pageSpecification();
    if (this.isAssetColumn(pageSpec.sortField)) {
      pageSpec.sortField = 'asset.' + pageSpec.sortField;
    }
    return pageSpec;
  }

  protected transformParamsToModelSetters(params: Params): {
    [key: string]: (value: any, formControl: FormControl, formControlName: string) => void
  } {
    return this.transformAllPropertiesToModelSetters();
  }

  protected transformAllPropertiesToModelSetters(): {
    [key: string]: (value: any, formControl: FormControl, formControlName: string) => void
  } {
    const self = this;
    return {
      ...super.transformAllPropertiesToModelSetters(),
      disposalDateGreaterThanOrEqualTo: (value: Date) => self.disposalDateGreaterThanOrEqualToFilter = value,
      disposalDateLessThanOrEqualTo: (value: Date) => self.disposalDateLessThanOrEqualToFilter = value
    };
  }

  allowsPool(element: Firearm): boolean {
    const hasEditPermission = this.userStorage.currentUserHasAnyAuthority(FIREARM_POOL_TYPE.editAuthorities);
    const hasViewPermission = this.userStorage.currentUserHasAnyAuthority(FIREARM_POOL_TYPE.viewAuthorities);
    return hasEditPermission || hasViewPermission;
  }

  isPoolEnabled(element: Firearm): boolean {
    return !element.personalFirearm;
  }

  poolRoute(element: Firearm): any[] {
    var routeBase: any[] = null;
    const hasEditPermission = this.userStorage.currentUserHasAnyAuthority(FIREARM_POOL_TYPE.editAuthorities);
    const useEditRoute = hasEditPermission;

    if (!element.firearmPoolExisting) {
      routeBase = FIREARM_POOL_TYPE.createRouteBase;
    } else {
      routeBase = useEditRoute ? FIREARM_POOL_TYPE.editRouteBase : FIREARM_POOL_TYPE.viewRouteBase;
    }
    return [...routeBase, element?.assetId];
  }

  poolRouteQueryParams() {
    return this.editRouteQueryParams();
  }
  get disposalDateGreaterThanOrEqualTo() {
    return this.searchForm && this.searchForm.get('disposalDateGreaterThanOrEqualTo');
  }

  get disposalDateLessThanOrEqualTo() {
    return this.searchForm && this.searchForm.get('disposalDateLessThanOrEqualTo');
  }

}
